import {
  BellOutlined,
  DollarOutlined,
  FundOutlined,
  LoginOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { SelectLang as UmiSelectLang, history, useModel } from '@umijs/max';
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Popover, Space, Tooltip } from 'antd';
import { getDashboardStats, type DashboardStats } from '@/services/dashboardAdmin';
import useResponsive from '@/hooks/useResponsive';

export type SiderTheme = 'light' | 'dark';

export const SelectLang: React.FC = () => (
  <UmiSelectLang
    style={{
      padding: 8, // bigger click area
      marginRight: 4,
    }}
  />
);

export const Question: React.FC = () => (
  <div
    style={{
      display: 'flex',
      height: 26,
    }}
    onClick={() => {
      window.open('https://pro.ant.design/docs/getting-started');
    }}
  >
    <QuestionCircleOutlined />
  </div>
);

const statsKeys: (keyof DashboardStats)[] = [
  'online_users',
  'today_registered',
  'today_logged_in',
  'app_recharge_pending',
  'app_withdraw_pending',
  'crypto_recharge_pending',
  'crypto_withdraw_pending',
  'viber_unread_messages',
  'viber_unread_users',
];

export const PendingCounters: React.FC = () => {
  const { isCompactHeader, isMobile } = useResponsive();
  const { setInitialState } = useModel('@@initialState');
  const [compactOpen, setCompactOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    online_users: 0,
    today_registered: 0,
    today_logged_in: 0,
    app_recharge_pending: 0,
    app_withdraw_pending: 0,
    crypto_recharge_pending: 0,
    crypto_withdraw_pending: 0,
    viber_unread_messages: 0,
    viber_unread_users: 0,
    shankoemee_online: { total: 0, real_users: 0, robots: 0 },
    boogyi_online: { total: 0, real_users: 0, robots: 0 },
  });

  const syncInitialState = useCallback(
    (next: DashboardStats) => {
      setStats(next);
      if (!setInitialState) {
        return;
      }
      setInitialState((prev: any) => {
        const prevStats: DashboardStats | undefined = prev?.dashboardStats;
        const hasChanged = !prevStats || statsKeys.some((key) => prevStats[key] !== next[key]);
        if (!hasChanged) {
          return prev;
        }
        return {
          ...prev,
          dashboardStats: next,
        };
      });
    },
    [setInitialState],
  );

  const fetchStats = async () => {
    try {
      const res = await getDashboardStats();
      if (res && (res as any).success && (res as any).data) {
        syncInitialState((res as any).data as DashboardStats);
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    fetchStats();
    const t = setInterval(fetchStats, 10000); // 10s refresh
    return () => clearInterval(t);
  }, [syncInitialState]);

  const statItems = [
    {
      title: '在线用户',
      value: stats.online_users,
      icon: <UserOutlined style={{ fontSize: 20, color: '#52c41a' }} />,
      color: '#52c41a',
      target: '/users/list',
    },
    {
      title: '今日注册',
      value: stats.today_registered,
      icon: <TeamOutlined style={{ fontSize: 20, color: '#1890ff' }} />,
      color: '#1890ff',
      target: '/users/list',
    },
    {
      title: '今日登录',
      value: stats.today_logged_in,
      icon: <LoginOutlined style={{ fontSize: 20, color: '#722ed1' }} />,
      color: '#722ed1',
      target: '/users/list',
    },
    {
      title: 'App充值',
      value: stats.app_recharge_pending,
      icon: <DollarOutlined style={{ fontSize: 20, color: '#faad14' }} />,
      color: '#faad14',
      target: '/finance/deposits',
    },
    {
      title: 'App提现',
      value: stats.app_withdraw_pending,
      icon: <WalletOutlined style={{ fontSize: 20, color: '#fa8c16' }} />,
      color: '#fa8c16',
      target: '/finance/withdraws',
    },
    {
      title: '币充值',
      value: stats.crypto_recharge_pending,
      icon: <FundOutlined style={{ fontSize: 20, color: '#13c2c2' }} />,
      color: '#13c2c2',
      target: '/finance/crypto-recharges',
    },
    {
      title: '币提现',
      value: stats.crypto_withdraw_pending,
      icon: <SwapOutlined style={{ fontSize: 20, color: '#eb2f96' }} />,
      color: '#eb2f96',
      target: '/finance/crypto-withdraws',
    },
    {
      title: 'Viber',
      value: stats.viber_unread_messages,
      icon: <MessageOutlined style={{ fontSize: 20, color: '#7360f2' }} />,
      color: '#7360f2',
      target: '/system-management/viber-support',
    },
  ];

  const attentionCount =
    stats.app_recharge_pending +
    stats.app_withdraw_pending +
    stats.crypto_recharge_pending +
    stats.crypto_withdraw_pending +
    stats.viber_unread_messages;

  if (isCompactHeader) {
    const compactContent = (
      <div className="bo-pending-popover" role="menu" aria-label="实时数据与待办">
        <div className="bo-pending-popover-title">
          <span>实时数据与待办</span>
          <span className="bo-pending-popover-updated">每 10 秒更新</span>
        </div>
        <div className="bo-pending-popover-grid">
          {statItems.map((item) => (
            <button
              key={item.title}
              type="button"
              className="bo-pending-popover-item"
              onClick={() => {
                setCompactOpen(false);
                if (item.target) {
                  history.push(item.target);
                }
              }}
              role="menuitem"
            >
              <span className="bo-pending-popover-icon" style={{ background: `${item.color}18` }}>
                {item.icon}
              </span>
              <span className="bo-pending-popover-copy">
                <strong style={{ color: item.color }}>{item.value}</strong>
                <small>{item.title}</small>
              </span>
            </button>
          ))}
        </div>
      </div>
    );

    return (
      <Popover
        content={compactContent}
        trigger="click"
        placement={isMobile ? 'bottom' : 'bottomRight'}
        overlayClassName="bo-pending-popover-overlay"
        open={compactOpen}
        onOpenChange={setCompactOpen}
      >
        <Badge count={attentionCount} overflowCount={99} size="small">
          <Button
            className="bo-header-icon-button"
            type="text"
            icon={<BellOutlined />}
            aria-label={`待办与实时数据${attentionCount > 0 ? `，${attentionCount} 项待处理` : ''}`}
          />
        </Badge>
      </Popover>
    );
  }

  return (
    <div className="bo-pending-counters" style={{ marginRight: 16 }}>
      <Space size={16}>
        {statItems.map((item, index) => {
          const content = (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 16px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 8,
                cursor: item.target ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                minWidth: 120,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${item.color}30`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => {
                if (item.target) {
                  history.push(item.target);
                }
              }}
            >
              <div
                style={{
                  marginRight: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `${item.color}15`,
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                }}
              >
                {item.icon}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: '600',
                    color: '#fff',
                    lineHeight: '22px',
                    fontFamily: 'DIN Alternate, Roboto, sans-serif',
                  }}
                >
                  {item.value}
                </span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: '14px' }}>
                  {item.title}
                </span>
              </div>
            </div>
          );

          if (item.title === 'Viber') {
            const tooltipContent = (
              <div>
                <div style={{ marginBottom: 4 }}>
                  <b>Viber 客服未读:</b>
                </div>
                <div>• 未读消息: {stats.viber_unread_messages}</div>
                <div>• 未读用户: {stats.viber_unread_users}</div>
                {stats.viber_unread_messages === 0 && (
                  <div style={{ fontSize: '10px', marginTop: 4, opacity: 0.6 }}>当前无未读</div>
                )}
              </div>
            );
            return (
              <Tooltip
                key={index}
                title={tooltipContent}
                color="#1f1f1f"
                overlayInnerStyle={{ padding: '8px 12px' }}
              >
                {content}
              </Tooltip>
            );
          }

          if (item.title === '在线用户' && stats.shankoemee_online) {
            const tooltipContent = (
              <div>
                <div style={{ marginBottom: 4 }}>
                  <b>Shankoemee 在线明细:</b>
                </div>
                <div>• 真实用户: {stats.shankoemee_online.real_users}</div>
                <div>• 机器人数: {stats.shankoemee_online.robots}</div>
                <div style={{ margin: '8px 0 4px' }}>
                  <b>Boogyi 在线明细:</b>
                </div>
                <div>• 真实用户: {stats.boogyi_online?.real_users || 0}</div>
                <div>• 机器人数: {stats.boogyi_online?.robots || 0}</div>
                <div style={{ fontSize: '10px', marginTop: 4, opacity: 0.6 }}>
                  更新时间:{' '}
                  {stats.boogyi_online?.last_updated
                    ? new Date(stats.boogyi_online.last_updated).toLocaleTimeString()
                    : stats.shankoemee_online.last_updated
                      ? new Date(stats.shankoemee_online.last_updated).toLocaleTimeString()
                      : 'N/A'}
                </div>
              </div>
            );
            return (
              <Tooltip
                key={index}
                title={tooltipContent}
                color="#1f1f1f"
                overlayInnerStyle={{ padding: '8px 12px' }}
              >
                {content}
              </Tooltip>
            );
          }

          return content;
        })}
      </Space>
    </div>
  );
};

// Optional: compose default right content with counters + helpers
const RightContent: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <PendingCounters />
    <Space size={8}>{/* <Question /> removed */}</Space>
  </div>
);

export default RightContent;
